import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Package, Box, Warehouse, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEMP_COLORS = {
  FROZEN:  { bg: "#dbeafe", border: "#3b82f6", fill: "#2563eb", text: "#1e40af", label: "Frozen" },
  COOLER:  { bg: "#d1fae5", border: "#10b981", fill: "#059669", text: "#065f46", label: "Cooler" },
  AMBIENT: { bg: "#fef3c7", border: "#f59e0b", fill: "#d97706", text: "#92400e", label: "Dry" },
  DOCK:    { bg: "#e5e7eb", border: "#6b7280", fill: "#4b5563", text: "#1f2937", label: "Dock" },
};

export default function FacilityMapPage() {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("facility");
  const [selectedZone, setSelectedZone] = useState(null);
  const [zoneDetail, setZoneDetail] = useState(null);

  const fetchZones = useCallback(async () => {
    try { const r = await axios.get(`${API}/wms/zones`); setZones(r.data); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchZones(); }, [fetchZones]);

  const openZone = async (zone) => {
    setSelectedZone(zone);
    try { const r = await axios.get(`${API}/wms/zones/${zone.zone_id}`); setZoneDetail(r.data); setView("zone"); }
    catch { toast.error("Failed to load zone"); }
  };

  const goBack = () => { setView("facility"); setSelectedZone(null); setZoneDetail(null); };

  const grouped = useMemo(() => {
    const g = { dock: [], cooler: [], freezer: [], dry: [], production: [] };
    zones.forEach(z => {
      if (z.room === "300") g.dock.push(z);
      else if (z.room === "301" || z.zone_id === "FG-STG-CLR") g.cooler.push(z);
      else if (z.room === "302" || z.zone_id === "FG-STG-FRZ") g.freezer.push(z);
      else if (z.room?.startsWith("400") || z.zone_id === "DRY-STG-IN" || z.zone_id === "DRY-STG-PROD") g.dry.push(z);
      else g.production.push(z);
    });
    return g;
  }, [zones]);

  if (loading) return <div className="flex items-center justify-center h-[calc(100vh-60px)]"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-zinc-600" /></div>;

  return (
    <div className="h-[calc(100vh-60px)] flex flex-col overflow-hidden" data-testid="facility-map-page">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-200 bg-white shrink-0">
        <div className="flex items-center gap-2">
          {view !== "facility" && <button onClick={goBack} className="p-1 hover:bg-zinc-100 rounded" data-testid="map-back-btn"><ArrowLeft className="w-4 h-4" /></button>}
          <Warehouse className="w-5 h-5 text-zinc-700" />
          <h1 className="text-sm font-bold text-zinc-900">{view === "facility" ? "Facility Map — 2520 Weir St" : selectedZone?.name}</h1>
        </div>
        <div className="flex items-center gap-3">
          {Object.entries(TEMP_COLORS).map(([k, v]) => (
            <span key={k} className="flex items-center gap-1 text-[10px]"><span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: v.fill }} />{v.label}</span>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-auto bg-zinc-100">
        {view === "facility" && <FacilityFloorPlan grouped={grouped} onZoneClick={openZone} />}
        {view === "zone" && zoneDetail && <ZoneDetailView zone={zoneDetail} onBack={goBack} />}
      </div>
    </div>
  );
}

function FacilityFloorPlan({ grouped, onZoneClick }) {
  const utilColor = (pct) => pct >= 90 ? "#ef4444" : pct >= 70 ? "#f59e0b" : "#22c55e";
  return (
    <div className="p-4 max-w-6xl mx-auto" data-testid="facility-floor-plan">
      <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-4">
        <div className="text-[10px] text-zinc-400 uppercase tracking-widest mb-3 text-center">Facility Floor Plan — Bird's Eye View</div>
        <div className="grid gap-3" style={{ gridTemplateColumns: "1fr 2fr 1fr", gridTemplateRows: "auto auto" }}>
          <RoomBlock title="Dry Storage" subtitle="Rooms 400A/400B" color={TEMP_COLORS.AMBIENT} zones={grouped.dry} onZoneClick={onZoneClick} utilColor={utilColor} />
          <RoomBlock title="Freezer" subtitle="Room 302 — 170' x 55' x 36'" color={TEMP_COLORS.FROZEN} zones={grouped.freezer} onZoneClick={onZoneClick} utilColor={utilColor} className="row-span-2" tall />
          <RoomBlock title="Production" subtitle="Rooms 304, 308, 310" color={TEMP_COLORS.COOLER} zones={grouped.production} onZoneClick={onZoneClick} utilColor={utilColor} compact />
          <RoomBlock title="Cooler" subtitle="Room 301 — 80' x 55'" color={TEMP_COLORS.COOLER} zones={grouped.cooler} onZoneClick={onZoneClick} utilColor={utilColor} />
          <RoomBlock title="Cold Dock" subtitle="Room 300 — 3 Doors" color={TEMP_COLORS.DOCK} zones={grouped.dock} onZoneClick={onZoneClick} utilColor={utilColor} compact />
        </div>
        <div className="mt-3 flex items-center justify-center gap-4 text-[10px] text-zinc-400">
          <span>Inbound: Dock → Cooler/Freezer</span><span className="text-zinc-300">|</span>
          <span>Production Pull: Storage → Staging → Production</span><span className="text-zinc-300">|</span>
          <span>Outbound: Storage → Dock → Ship</span>
        </div>
      </div>
    </div>
  );
}

function RoomBlock({ title, subtitle, color, zones, onZoneClick, utilColor, className = "", compact, tall }) {
  const totalCap = zones.reduce((s, z) => s + (z.capacity || 0), 0);
  const totalOcc = zones.reduce((s, z) => s + (z.pallet_count || 0), 0);
  const pct = totalCap > 0 ? Math.round((totalOcc / totalCap) * 100) : 0;
  const utilPct = (z) => z.capacity > 0 ? Math.round((z.pallet_count / z.capacity) * 100) : 0;
  return (
    <div className={`rounded-lg border-2 p-2.5 ${className}`} style={{ borderColor: color.border, backgroundColor: color.bg + "80" }} data-testid={`room-${title.toLowerCase().replace(/ /g, "-")}`}>
      <div className="flex items-center justify-between mb-1.5">
        <div><div className="text-xs font-bold" style={{ color: color.text }}>{title}</div><div className="text-[10px] text-zinc-500">{subtitle}</div></div>
        <div className="text-right"><div className="text-xs font-bold" style={{ color: utilColor(pct) }}>{pct}%</div><div className="text-[10px] text-zinc-500">{totalOcc}/{totalCap}</div></div>
      </div>
      <div className="w-full h-1.5 bg-white/60 rounded-full mb-2 overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: utilColor(pct) }} /></div>
      <div className={`flex flex-wrap gap-1 ${compact ? "" : tall ? "max-h-[280px] overflow-y-auto" : "max-h-[160px] overflow-y-auto"}`}>
        {zones.map(z => {
          const zp = utilPct(z);
          return (
            <button key={z.zone_id} onClick={() => onZoneClick(z)} data-testid={`zone-chip-${z.zone_id}`}
              className="flex items-center gap-1 px-2 py-1 rounded border bg-white/80 hover:bg-white transition-colors text-left group" style={{ borderColor: color.border + "60" }}>
              <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: utilColor(zp) }} />
              <div><div className="text-[10px] font-semibold text-zinc-800 leading-tight">{z.zone_id}</div><div className="text-[9px] text-zinc-500 leading-tight">{z.pallet_count || 0}p</div></div>
              <ChevronRight className="w-3 h-3 text-zinc-300 group-hover:text-zinc-500 ml-auto shrink-0" />
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ZoneDetailView({ zone, onBack }) {
  const locations = zone.locations || [];
  const occupied = locations.filter(l => l.inventory?.length > 0);
  const tc = TEMP_COLORS[zone.temp_class] || TEMP_COLORS.AMBIENT;
  return (
    <div className="p-4 max-w-4xl mx-auto space-y-4" data-testid="zone-detail-view">
      <Card className="border-l-4" style={{ borderLeftColor: tc.fill }}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div><h2 className="text-base font-bold" style={{ color: tc.text }}>{zone.zone_id} — {zone.name}</h2><div className="text-xs text-zinc-500 mt-0.5">{zone.temp_range} | {zone.storage_type} | Room {zone.room}</div></div>
            <div className="text-right"><div className="text-lg font-bold">{zone.inventory_count}</div><div className="text-xs text-zinc-500">pallets</div></div>
          </div>
        </CardContent>
      </Card>
      <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 gap-1.5">
        {locations.map(loc => {
          const inv = loc.inventory?.[0]; const hasInv = !!inv; const isHold = inv?.status === "HOLD";
          return (
            <div key={loc.location_code} data-testid={`floor-pos-${loc.location_code}`}
              className={`aspect-square rounded-md border flex flex-col items-center justify-center text-[9px] cursor-default ${isHold ? "bg-red-100 border-red-300" : hasInv ? "bg-blue-100 border-blue-300" : "bg-zinc-50 border-zinc-200"}`}
              title={hasInv ? `${inv.item_name}\nLot: ${inv.lot_number}\nQty: ${inv.quantity} ${inv.uom}` : "Empty"}>
              <span className="font-mono font-bold leading-tight">{loc.location_code.split("-").pop()}</span>
              {hasInv && <span className="truncate max-w-full px-0.5 text-[8px] text-zinc-600">{inv.item_id}</span>}
            </div>
          );
        })}
      </div>
      {occupied.length > 0 && (
        <div className="border border-zinc-200 rounded-lg overflow-hidden">
          <table className="w-full text-xs"><thead className="bg-zinc-50"><tr>
            <th className="text-left px-2 py-1.5 font-medium">Location</th><th className="text-left px-2 py-1.5 font-medium">LP</th>
            <th className="text-left px-2 py-1.5 font-medium">Item</th><th className="text-left px-2 py-1.5 font-medium">Lot</th>
            <th className="text-right px-2 py-1.5 font-medium">Qty</th><th className="text-left px-2 py-1.5 font-medium">Status</th>
          </tr></thead><tbody>
            {occupied.map(loc => loc.inventory.map(inv => (
              <tr key={inv.inventory_id} className="border-t border-zinc-100">
                <td className="px-2 py-1 font-mono text-[10px]">{loc.location_code}</td>
                <td className="px-2 py-1 font-mono text-[10px]">{inv.lp_number?.slice(0, 16)}</td>
                <td className="px-2 py-1 truncate max-w-[120px]">{inv.item_name}</td>
                <td className="px-2 py-1">{inv.lot_number}</td>
                <td className="px-2 py-1 text-right font-medium">{inv.quantity}</td>
                <td className="px-2 py-1"><span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${inv.status === "HOLD" ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>{inv.status}</span></td>
              </tr>
            )))}
          </tbody></table>
        </div>
      )}
    </div>
  );
}
